import { PropsWithChildren, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useFeature, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { AppAside, AppTool, useAppAsideControl } from '@treams/treams-ui';

import type { AgentChatRequest } from '../ai-agent/components/AgentChatDetail';
import { TreamsAIPanel } from '../ai-agent/components/TreamsAIPanel';
import { ContextSidebarToggle, HelpdeskToggle, TreamsAIToggle } from './TreamsAIToggle';
import { AiPromptRequest, RightSectionContext, RightSectionPanel } from './rightSectionContext';
import { CrispDockStyles, useCrispDock } from './useCrispDock';

const RailButtons = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${(props) => props.theme.tokens.gap.element}px;
`;

const Pane = styled.div<{ $open: boolean }>`
	display: ${(props) => (props.$open ? 'flex' : 'none')};
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	height: 100%;
	overflow: hidden;
	overscroll-behavior: contain;
`;

export function RightSection(props: PropsWithChildren) {
	const hasAiInsights = useFeature(graphql.FeatureName.ConversationAiInsights);
	const hasAiAgent = useFeature(graphql.FeatureName.AiAgent);
	const { isWorkspaceSuperuser } = useViewerRoles();
	const hasAi = isWorkspaceSuperuser || (hasAiInsights && hasAiAgent);

	const { open, setOpen } = useAppAsideControl();
	const [panel, setPanel] = useState<RightSectionPanel>(null);
	const [hasOpenedAi, setHasOpenedAi] = useState(false);
	const [aiRequest, setAiRequest] = useState<AgentChatRequest>();
	const [hasContext, setHasContext] = useState(false);
	const [contextSlot, setContextSlot] = useState<HTMLElement | null>(null);
	const [helpPane, setHelpPane] = useState<HTMLElement | null>(null);

	useCrispDock(panel, setPanel, helpPane, open);

	const openContext = useCallback(() => setPanel('context'), []);
	const close = useCallback(() => setPanel(null), []);

	const openAi = useCallback((request?: AiPromptRequest) => {
		setPanel('ai');
		setHasOpenedAi(true);
		if (request) {
			setAiRequest({ ...request, id: Date.now() });
		}
	}, []);

	const toggle = useCallback((next: Exclude<RightSectionPanel, null>) => {
		setPanel((current) => (current === next ? null : next));
		if (next === 'ai') {
			setHasOpenedAi(true);
		}
	}, []);

	useLayoutEffect(() => {
		setOpen(panel !== null);
	}, [panel, setOpen]);

	useLayoutEffect(() => {
		if (!hasContext && panel === 'context') {
			setPanel(null);
		}
	}, [hasContext, panel]);

	const api = useMemo(
		() => ({
			slot: contextSlot,
			panel,
			hasAi,
			setHasContext,
			openContext,
			openAi,
			close,
		}),
		[close, contextSlot, hasAi, openAi, openContext, panel],
	);

	return (
		<RightSectionContext.Provider value={api}>
			<CrispDockStyles />
			{props.children}
			<AppTool>
				<RailButtons>
					{hasAi && <TreamsAIToggle open={panel === 'ai'} onClick={() => toggle('ai')} />}
					{hasContext && (
						<ContextSidebarToggle open={panel === 'context'} onClick={() => toggle('context')} />
					)}
					<HelpdeskToggle open={panel === 'help'} onClick={() => toggle('help')} />
				</RailButtons>
			</AppTool>
			<AppAside>
				{hasAi && hasOpenedAi && (
					<Pane $open={panel === 'ai'}>
						<TreamsAIPanel request={aiRequest} />
					</Pane>
				)}
				<Pane ref={setContextSlot} $open={panel === 'context'} />
				<Pane ref={setHelpPane} $open={panel === 'help'} />
			</AppAside>
		</RightSectionContext.Provider>
	);
}
